// src/client/lib/session-storage.ts
//
// 認証 token / user の永続化 wrapper。
//
// 設計方針 (2026-07-26 更新: owner 承認のもと「1 日の永続ログイン」に変更):
//  - **localStorage を採用**。 tab / link / ブラウザ再起動をまたいでもログインを維持する。
//    (旧: sessionStorage = tab を閉じると自動クリア。 別タブ / リンクで毎回再ログインが必要で
//     業務 UX の負荷が高かったため、 owner 承認で変更。 docs/plans/phase-b-frontend-mvp-plan.md Q2 を再評価)
//  - **有効期限は最大 1 日**。 server の JWT exp が 24h (adapters/auth.ts DEFAULT_EXPIRES_IN_SEC)
//    のため、 期限切れ token は自動失効する。 client 側でも loadAuth で expiresAt を検査し、
//    期限切れなら clear して null を返す (期限切れ token を保持し続けない)。
//  - XSS リスクは CSP + 監査 + 24h の短命 token で軽減。 将来 HttpOnly cookie 化する場合は
//    server の Set-Cookie + CSRF 対策とセットで別途設計する。
//  - JSON parse 失敗時は null fallback (壊れた session を強制クリアに繋げる)。
//
// 参照:
//  - src/server/adapters/auth.ts (issueToken: 24h JWT)
//  - docs/plans/phase-b-frontend-mvp-plan.md Q2 (旧 sessionStorage 決定の再評価)

import type { AuthUser } from "../providers/AuthProvider";

const TOKEN_KEY = "housing-os:auth:token";
const EXPIRES_KEY = "housing-os:auth:expiresAt";
const USER_KEY = "housing-os:auth:user";

// storage backend。 localStorage を使い、 利用不可 (private mode 等) の場合は
// 例外を握って memory-less 運用 (毎回ログイン) に degrade する。
function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export type StoredAuth = {
  readonly token: string;
  /**
   * 有効期限。 **server は Unix 秒 (number) を返す** (src/server/adapters/auth.ts issueToken:
   * `expiresAt: expSec`)。 過去の型宣言は string としていたが実体は number であり、
   * Web Storage は文字列化するため read 側で寛容に解釈する (Codex #613 P1)。
   */
  readonly expiresAt: string | number;
  readonly user: AuthUser;
};

/**
 * 保存された expiresAt を epoch ミリ秒に変換する。
 * 受理する形式:
 *   - Unix 秒 (server の実際の形式。 例 "1785144753")
 *   - Unix ミリ秒 (防御的)
 *   - ISO 8601 文字列 (将来 API が変わった場合の防御)
 * 解釈できない場合は null (= 期限判定不能) を返す。
 */
function parseExpiresAtMs(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  // 数値なら epoch。 桁で 秒 / ミリ秒 を判別 (1e12 未満は秒とみなす)。
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n < 1e12 ? n * 1000 : n;
  }
  const parsed = new Date(trimmed).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

export function saveAuth(payload: StoredAuth): void {
  const s = store();
  if (!s) return; // private mode 等: memory のみで運用
  try {
    s.setItem(TOKEN_KEY, payload.token);
    s.setItem(EXPIRES_KEY, String(payload.expiresAt));
    s.setItem(USER_KEY, JSON.stringify(payload.user));
  } catch {
    // quota / disabled 等は無視 (未認証扱いに degrade)
  }
}

export function loadAuth(): StoredAuth | null {
  const s = store();
  if (!s) return null;
  try {
    const token = s.getItem(TOKEN_KEY);
    const expiresAt = s.getItem(EXPIRES_KEY);
    const userStr = s.getItem(USER_KEY);
    if (!token || !expiresAt || !userStr) return null;
    // 期限切れ token は保持しない (最大 1 日、 server JWT exp と整合)。
    // 解釈できない形式の場合は **clear せず通す** (fail-open): 期限判定は server の
    // JWT exp が最終権威であり、 client の parse 失敗で正当な session を落とさない。
    // (Codex #613 P1: 旧実装は Unix 秒を new Date() に渡して NaN → 常時 clear → ログイン不能)
    const expMs = parseExpiresAtMs(expiresAt);
    if (expMs !== null && expMs <= Date.now()) {
      clearAuth();
      return null;
    }
    const user = JSON.parse(userStr) as AuthUser;
    return { token, expiresAt, user };
  } catch {
    return null;
  }
}

export function clearAuth(): void {
  const s = store();
  if (!s) return;
  try {
    s.removeItem(TOKEN_KEY);
    s.removeItem(EXPIRES_KEY);
    s.removeItem(USER_KEY);
  } catch {
    // ignore
  }
}
